import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { ViewConnectionsList } from './connections/view-connections-list'
import { ConnectionDrawerNew, ConnectionDrawerEdit } from './connections/connection-drawer'
import { ViewCourseIntegration } from './courses/view-course-integration'
import { ViewBuildLog } from './packages/view-build-log'
import { ViewAccountLinks } from './links/view-account-links'
import { ViewActiveSessions } from './sessions/view-active-sessions'
import { ViewSessionDetail } from './sessions/view-session-detail'
import { ViewActivityFeed } from './activity/view-activity-feed'
import { ViewPlanUsage } from './usage/view-plan-usage'
import { ViewCustomerSettings } from './settings/view-customer-settings'
import { ViewCourses } from './courses/view-courses'
import { ViewPresets } from './presets/view-presets'
import { PresetEditorNew, PresetEditorEdit } from './presets/preset-editor'
import { RoleSysLmsPreset } from '../../role/components/role-sys'
import { NoAccess } from '../../role/components/no-access'
import { useAuthContext } from '../../context/auth.context'
import { NotFoundPage } from '../../page/not-found'
import { claimsKnown, lmsAdminVisible } from './lms-admin-gate'
import './lms-admin.scss'

// The presets pages: the server's preset routes need a system administrator
// (or root) as well as the SYS_LMS_PRESET scope, so the page does too.
const PresetsGate = ({ children }) => {
  const { state } = useAuthContext()
  if (!(state.isSysRoot || state.isSysAdmin)) return <NoAccess what="LMS presets" />
  return <RoleSysLmsPreset accessRead fallback={<NoAccess what="LMS presets" />}>{children}</RoleSysLmsPreset>
}

// LayoutLms hosts the LMS Connector admin surfaces (docs/08), composed into
// LayoutOrg like every other org feature. Surfaces land one PR at a time in
// the docs/08 build order; the connections list is the entry point.
//
// The admin's content frame clips (page/layout.js, overflow-hidden), so every
// section scrolls a pane of its own inside it: the SSO and provider pages
// FleetPage's .sw-pane, the dashboard its Grid. The LMS pages scroll .lms-pane
// (lms-admin.scss, .sw-pane's rule), drawn only under /org/lms, with the page
// directly inside it.
//
// /org/lms itself goes to Connections, the section's landing page. An address
// under /org/lms that no page has shows the admin's own not-found page (the
// last route): the app's check (page/not-found.js UnknownRoute) knows /org/lms
// as a section and leaves it to this layout.
//
// While the staff-only switch is closed (C1, staff-only.js) the pages exist
// only for system administrators and root. For anyone else every address
// under /org/lms, the section's own included, is the not-found page an
// unknown address gets: absent, not a page that refuses. Until the session's
// claims arrive (a reload, before the first refresh) nothing is drawn, so a
// system administrator never sees a not-found flash.
//
// The LMS's own API key pages (/org/lms/api-keys, a connection's
// /api-keys) are retired with their LMS routes (Phase D D7): their addresses
// are the not-found page below. A connection's keys are a panel in its
// drawer; the organization's are /org/api-keys.
export const LayoutLms = () => {
  const { state } = useAuthContext()

  if (!lmsAdminVisible(state)) {
    return <Route path="/org/lms" render={({ location }) => (claimsKnown(state) ? <NotFoundPage pathname={location.pathname} /> : null)} />
  }

  return (
    <Route path="/org/lms">
      <div className="lms-pane">
        <Switch>
          <Route exact path="/org/lms/connections" component={ViewConnectionsList} />
          <Route exact path="/org/lms/connections/new" component={ConnectionDrawerNew} />
          <Route exact path="/org/lms/connections/:connectionId(\d+)" component={ConnectionDrawerEdit} />
          <Route exact path="/org/lms/courses/:nodeId(\d+)/integration" component={ViewCourseIntegration} />
          <Route exact path="/org/lms/packages/:packageId(\d+)/build-log" component={ViewBuildLog} />
          <Route exact path="/org/lms/account-links" component={ViewAccountLinks} />
          <Route exact path="/org/lms/sessions" component={ViewActiveSessions} />
          <Route exact path="/org/lms/sessions/:sessionRef([\w-]+)" component={ViewSessionDetail} />
          <Route exact path="/org/lms/activity" component={ViewActivityFeed} />
          <Route exact path="/org/lms/usage" component={ViewPlanUsage} />
          <Route exact path="/org/lms/settings" component={ViewCustomerSettings} />
          <Route exact path="/org/lms/courses" component={ViewCourses} />
          {/* Presets are platform templates: the SYS_LMS_PRESET system scope, not
              an organization role. The server gates the routes the same way. */}
          <Route exact path="/org/lms/presets">
            <PresetsGate><ViewPresets /></PresetsGate>
          </Route>
          <Route exact path="/org/lms/presets/new">
            <PresetsGate><PresetEditorNew /></PresetsGate>
          </Route>
          <Route exact path="/org/lms/presets/:presetId(\d+)">
            <PresetsGate><PresetEditorEdit /></PresetsGate>
          </Route>
          {/* The section's own address is its landing page (with or without a closing slash). */}
          <Redirect exact from="/org/lms" to="/org/lms/connections" />
          <Route render={({ location }) => <NotFoundPage pathname={location.pathname} />} />
        </Switch>
      </div>
    </Route>
  )
}
